/**
 * sl-vw37.1.3: Frontend Vertical Definition Registry
 *
 * Frontend-side registry for vertical definitions, analogous to backend VerticalRegistry.
 * Each vertical module calls registerVertical() to add itself.
 *
 * SOLID:
 * - S: Only vertical definition registration
 * - O: New verticals register themselves — no modification needed here
 */
import type { ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface FrontendVerticalDefinition {
  /** Unique vertical identifier (matches backend IVerticalDefinition.id) */
  id: string;
  /** Feature key for plan-based gating (matches backend plan_features.feature_key) */
  featureKey: string;
  /** Template ID for provisioning (null if no template) */
  templateId: string | null;
  /** Tile display data for workspace settings integration list */
  tile: {
    name: string;
    description: string;
    icon: ReactNode;
  };
  /** CSS class name toggled on body when vertical is enabled */
  gateClassName: string;
  /** Order in integration list (lower = earlier) */
  order: number;
  /** Optional metadata for addon verticals */
  metadata?: {
    /** True if this is an addon that requires other verticals to function */
    addon?: boolean;
    /** True if at least one other vertical must be active for this to work */
    requiresActiveVertical?: boolean;
  };
}

// ============================================================================
// Registry
// ============================================================================

/**
 * Mutable array of registered vertical definitions.
 * Populated at module init time by vertical-specific modules.
 */
export const VERTICAL_DEFINITIONS: FrontendVerticalDefinition[] = [];

/**
 * Register a vertical definition. Called by vertical-specific modules.
 * Maintains sorted order by the `order` field.
 */
export function registerVertical(def: FrontendVerticalDefinition): void {
  if (VERTICAL_DEFINITIONS.some(v => v.id === def.id)) {
    throw new Error(`Vertical already registered: ${def.id}`);
  }
  VERTICAL_DEFINITIONS.push(def);
  VERTICAL_DEFINITIONS.sort((a, b) => a.order - b.order);
}
